import React, {useState} from "react";
import {BasicSearchType} from "../../../../../types/common/basic";
import {initSearchState} from "../../../../../state/search";
import {useReadAccountAddressList} from "../hooks";
import {SubmitHandler, useForm, useWatch} from "react-hook-form";
import EmptyTableRow from "../../../../common/EmptyTableRow";
import PaginationSub from "../../../../../components/common/pagination/PaginationSub";
import {useModal} from "../../../../../hooks";
import {createAddressInit} from "../state";
import {CreateAddress, SearchAddressResType} from "../interface";
import DaumPost from "../../../../../components/common/DaumPost";
import {useSaveAccountAddressInfo} from "../hooks/useSaveAccountAddressInfo";
import {useUpdateAccountAddressInfo} from "../hooks/useUpdateAccountAddressInfo"; // 내부수정: update hook import 추가
import {useQueryClient} from "@tanstack/react-query";
import {useUser} from "../../../../../util/userContext/UserContext";
import useDeleteAccountAddressInfo from "../hooks/useDeleteAccountAddressInfo";
import AuthorityChecker from "../../../../../components/common/authorityGuard/AuthorityChecker";

type Props = {
    id: string;
    accountName?: string; // 내부수정: 관련 거래처명 표시용
    checkedAddress: SearchAddressResType[]
    setCheckedAddress: React.Dispatch<React.SetStateAction<SearchAddressResType[]>>
};

// 내부수정: initialData prop 추가 (수정 시 기존 데이터 세팅용)
interface InnerProps extends Props {
    initialData?: CreateAddress
    modalDepth?: number // 내부수정: 중첩 모달 depth 계산용
}

// TODO: 거래처 상세 > 주소 > 생성 모달 임시 인터페이스
const CreateAddressInner = ({id, accountName, checkedAddress, setCheckedAddress, initialData}: InnerProps) => {
    const qc = useQueryClient();
    const {user} = useUser()
    const {handleModal} = useModal();
    const {resetModal} = useModal();

    const {register, handleSubmit, control, reset, setValue,} = useForm({
        // 내부수정: 수정 시 initialData로 초기값 세팅, 없으면 createAddressInit 사용
        defaultValues: initialData ?? createAddressInit,
    });

    const watchCreateData = {
        ...createAddressInit,
        ...useWatch<CreateAddress>({control})
    }

    const {mutateAsync: mutateAsyncSaveAccountAddressInfo} = useSaveAccountAddressInfo()
    // 내부수정: update hook 추가
    const {mutateAsync: mutateAsyncUpdateAccountAddressInfo} = useUpdateAccountAddressInfo()

    const onCancel = () => {
        reset()
        resetModal(1)
    }

    const onConfirm = () => {
        const param: CreateAddress = {
            ...watchCreateData,
            _new_l_customer_value: id,
            createdByValue: user ? user.crmId : ""
        }

        // 내부수정: new_addressid 유무로 insert/update 분기
        const apiCall = param.new_addressid
            ? mutateAsyncUpdateAccountAddressInfo(param)
            : mutateAsyncSaveAccountAddressInfo(param)

        apiCall.then(res => {
            if (res.data.code === "0000") {
                handleModal({
                    isOpen: true,
                    contents: "저장되었습니다.",
                    type: "alert",
                    closeAction: () => {
                        onCancel()
                        qc.invalidateQueries({queryKey: ["readAccountAddressList"]})
                        setCheckedAddress([])
                    },
                    depth: 2
                })
            }
        })
    }

    return <>
        <table className="table table-bordered my_table table_detail">
            <tbody>
            <tr>
                {/* 내부수정: 관련 거래처 표시(읽기 전용) */}
                <th style={{width: "16.66%"}}>관련 거래처</th>
                <td className="p-4px">
                    <input type="text"
                        className="form-control"
                        value={accountName ?? ""}
                        disabled/>
                </td>
            </tr>
            <tr>
                <th style={{width: "16.66%"}}>이름</th>
                <td className="p-4px" colSpan={3}>
                    <input type="text"
                           className="form-control"
                           value={watchCreateData.newName}
                           {...register("newName")}/>
                </td>
            </tr>
            <tr>
                <th>주소</th>
                <td className="p-4px" colSpan={3}>
                    <div className="d-flex">
                        {/* 내부수정: 우편번호 placeholder 추가, disable 처리 */}
                        <input type="text"
                               className="form-control w-150px h-34px"
                               placeholder="우편번호"
                               value={watchCreateData.new_txt_postalcode}
                               disabled/>
                        <DaumPost onCompleteCallback={(data) => {
                            setValue("new_txt_postalcode", data.zipcode);
                            setValue("new_txt_line1", data.address);
                        }}/>
                        {/* 내부수정: 주소1 disable 처리 */}
                        <input type="text"
                               className="form-control ms-5px h-34px"
                               value={watchCreateData.new_txt_line1}
                               disabled/>
                    </div>
                    <div className="d-flex mt-6px">
                        {/* 내부수정: 상세 주소 placeholder 추가 */}
                        <input type="text"
                               className="form-control h-34px"
                               placeholder="상세 주소"
                               value={watchCreateData.new_txt_line2}
                               {...register("new_txt_line2")}/>
                    </div>
                </td>
            </tr>
            </tbody>
        </table>

        <div className="mt-3 d-flex align-items-center justify-content-end">
            <button
                type="button"
                className="btn btn-outline-primary w-100px h-38px my-0"
                onClick={onCancel}
            >
                취소
            </button>
            <button
                type="button"
                className="btn btn-dark w-100px h-38px my-0 ms-3"
                onClick={onConfirm}
            >
                저장
            </button>
        </div>
    </>
}

/**
 * 주소 탭
 * @param props
 * @returns
 */
export const AddressTab = ({id, accountName, checkedAddress, setCheckedAddress}: Props) => {
    const {handleModal} = useModal();

    const [searchOption, setSearchOption] =
        useState<BasicSearchType>(initSearchState);
    const {data} = useReadAccountAddressList({
        searchData: {
            accountId: id,
            ...searchOption,
        },
    });

    const {mutate: mutateDeleteAccountAddressInfo} = useDeleteAccountAddressInfo({afterCallback: () => setCheckedAddress([])})

    const {register, handleSubmit, reset} = useForm({
        defaultValues: searchOption,
    });

    const clickAllCheck = () => {
        if (isAllCheck) {
            const list = checkedAddress
                .filter(el => !data?.SearchAccountAddressRes.map(el2 => el2.new_addressid).includes(el.new_addressid))

            setCheckedAddress(list)
        } else {
            // @ts-ignore
            const list = [...new Set([...checkedAddress, ...data?.SearchAccountAddressRes])]

            setCheckedAddress(list)
        }
    }

    const isAllCheck = data?.SearchAccountAddressRes.length !== 0 && data?.SearchAccountAddressRes
        .map(el => el.new_addressid)
        .every(el => checkedAddress.map(el => el.new_addressid).includes(el));

    const clickCheck = (el: SearchAddressResType) => {
        if (checkedAddress.map(el2 => el2.new_addressid).includes(el.new_addressid)) {
            const list = checkedAddress.filter(el2 => el.new_addressid !== el2.new_addressid)

            setCheckedAddress(list)
        } else {
            setCheckedAddress([...checkedAddress, el])
        }
    }

    const onSubmit: SubmitHandler<BasicSearchType> = (submitData) => {
        setCheckedAddress([])
        setSearchOption(submitData);
    };

    const clickPagination = (i: number) => {
        setSearchOption({...searchOption, pageNo: i})
    }

    const btnCreate = () => {
        handleModal({
            isOpen: true,
            // 내부수정: 관련 거래처명
            contents: <CreateAddressInner id={id} 
                                          accountName={accountName} 
                                          checkedAddress={checkedAddress}
                                          setCheckedAddress={setCheckedAddress}/>,
            type: "",
            title: "주소 생성",
            modalSize: "lg",
        })
    }

    const btnDelete = () => {
        if (checkedAddress.length === 0) {
            handleModal({
                isOpen: true,
                contents: "삭제할 항목을 선택해주세요.",
                type: "alert"
            })

            return;
        }

        handleModal({
            isOpen: true,
            contents: "삭제하시겠습니까?",
            type: "confirm",
            callback: () => {
                mutateDeleteAccountAddressInfo(checkedAddress.map(el => ({id: el.new_addressid})))
                console.log(checkedAddress)
            }
        })
    }

    // 내부수정: 로우 클릭 시 수정 모달 오픈
    const btnModify = (item: SearchAddressResType) => {
        handleModal({
            isOpen: true,
            contents: <CreateAddressInner
                id={id}
                accountName={accountName}
                checkedAddress={checkedAddress}
                setCheckedAddress={setCheckedAddress}
                initialData={{
                    _new_l_customer_value: item._new_l_customer_value,
                    createName: item.createName,
                    createdByValue: item._createdby_value,
                    createdon: item.createdon,
                    newName: item.newName,
                    new_addressid: item.new_addressid,
                    new_txt_line1: item.new_txt_line1,
                    new_txt_line2: item.new_txt_line2,
                    new_txt_postalcode: item.new_txt_postalcode,
                    statecode: item.statecode,
                    statuscode: String(item.statuscode)
                }}
            />,
            type: "",
            title: "주소 수정",
            modalSize: "lg",
        })
    }

    return (
        <>
            <form className="content_wrap" onSubmit={handleSubmit(onSubmit)}>
                <div className="fiter_top">
                    <h3 className="filter_heading">주소 검색</h3>
                    <div className="filter_toolbar"></div>
                </div>
                <div className="row">
                    <div className="col-6">
                        <div className="frm_row search justify-content-start">
                            <label className="form-label frm_label">검색어</label>
                            <select
                                className="form-select w-150px"
                                {...register("keywordType")}
                            >
                                <option value="0">전체</option>
                                <option value="1">이름</option>
                            </select>
                            <input type="text"
                                   className="form-control w-250px me-0"
                                   {...register("keyword")}
                            />
                            <button className="btn btn-info btn_search">
                                <img
                                    src={
                                        process.env.PUBLIC_URL + "/assets/images/icon_search.png"
                                    }
                                    alt=""
                                />
                                조회
                            </button>
                        </div>
                    </div>
                    <div className="col-6"></div>
                </div>
            </form>
            <div className="content_wrap">
                <div className="table_top">
                    <h6 className="table_stats">
                        주소 목록(<span>{data?.totalCount ?? 0}</span>건)
                    </h6>
                    <div className="table_toolbar">
                        <AuthorityChecker authorityNames={['clientWriteYn']}>
                            <button className="btn btn-primary px-0" onClick={btnCreate}>주소 생성</button>
                            <button className="btn btn-outline-dark w-100px px-0"
                                    onClick={btnDelete}>
                                <img src={process.env.PUBLIC_URL + "/assets/images/icon_cross.png"} alt=""/>
                                선택삭제
                            </button>
                        </AuthorityChecker>
                    </div>
                </div>

                <div className="table_scroll">
                    <table className="table table-bordered my_table">
                        <thead>
                        <tr>
                            <AuthorityChecker authorityNames={['clientWriteYn']}>
                                <th>
                                    <div className="checkbox_outer">
                                        <input className="form-check-input"
                                               type="checkbox"
                                               onClick={clickAllCheck}
                                               checked={isAllCheck}/>
                                    </div>
                                </th>
                            </AuthorityChecker>
                            <th>이름</th>
                            <th>우편번호</th>
                            <th>주소1</th>
                            <th>주소2</th>
                            <th>생성자</th>
                            <th>생성일자</th>
                            <th>상태</th>
                        </tr>
                        </thead>
                        <tbody>
                        {data?.SearchAccountAddressRes.length === 0 ? (
                            <EmptyTableRow colSpan={8}/>
                        ) : (
                            data?.SearchAccountAddressRes.map((item, index) => (
                                // 내부수정: 로우 클릭 시 수정 모달 오픈
                                <tr key={index}
                                    onClick={() => btnModify(item)}
                                    className={`clickable ${checkedAddress.map(el => el.new_addressid).includes(item.new_addressid) ? "lightblue" : ""}`}
                                >
                                    <AuthorityChecker authorityNames={['clientWriteYn']}>
                                        <td>
                                            <div className="checkbox_outer">
                                                <input className="form-check-input"
                                                       type="checkbox"
                                                       checked={checkedAddress.map(el => el.new_addressid).includes(item.new_addressid)}
                                                       // 내부수정: 체크박스 클릭 시 로우 클릭 이벤트 전파 차단
                                                       onClick={(e) => {
                                                           e.stopPropagation()
                                                           clickCheck(item)
                                                       }}/>
                                            </div>
                                        </td>
                                    </AuthorityChecker>
                                    <td className="text-center">{item?.newName ?? "-"}</td>
                                    <td className="text-center">{item?.new_txt_postalcode ?? "-"}</td>
                                    <td className="text-center">{item?.new_txt_line1 ?? "-"}</td>
                                    <td className="text-center">{item?.new_txt_line2 ?? "-"}</td>
                                    <td className="text-center">{item?.createName ?? "-"}</td>
                                    <td className="text-center">{item?.createdon ?? "-"}</td>
                                    <td className="text-center">{item?.statuscode ?? "-"}</td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>
                </div>

                <PaginationSub
                    totalCount={data?.totalCount ?? 0}
                    currentPage={searchOption.pageNo}
                    onChangeCallback={(i: number) => clickPagination(i)}
                />
            </div>
        </>
    );
};